import Component from "@glimmer/component";
import { cached } from "@glimmer/tracking";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import DEmptyState from "discourse/ui-kit/d-empty-state";
import dAvatar from "discourse/ui-kit/helpers/d-avatar";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dEmoji from "discourse/ui-kit/helpers/d-emoji";
import dIcon from "discourse/ui-kit/helpers/d-icon";
import { i18n } from "discourse-i18n";

const MAX_AVATARS = 5;

export default class ChatChannelEmptyState extends Component {
  @service chatApi;
  @service currentUser;

  @cached
  get memberships() {
    return this.chatApi.listChannelMemberships(this.args.channel.id);
  }

  get otherMemberships() {
    return this.memberships.items.filter(
      (membership) => membership.user.id !== this.currentUser?.id
    );
  }

  get memberCount() {
    const total = this.args.channel.membershipsCount ?? 0;
    const count = this.args.channel.isFollowing ? total - 1 : total;
    return Math.max(0, count);
  }

  get channelIcon() {
    const { emoji, chatable } = this.args.channel;

    if (this.isOneToOneDirectMessage && chatable?.users?.[0]) {
      return dAvatar(chatable.users[0], { imageSize: "extra_large" });
    }

    const icon = (emoji && dEmoji(emoji)) || dIcon("d-chat");

    if (!chatable?.color) {
      return icon;
    }

    return trustHTML(`<span style="color: #${chatable.color}">${icon}</span>`);
  }

  get isOneToOneDirectMessage() {
    const { isDirectMessageChannel, chatable } = this.args.channel;
    return isDirectMessageChannel && !chatable?.group;
  }

  get title() {
    if (this.isOneToOneDirectMessage) {
      return i18n("chat.channel.empty_state.direct_message_title", {
        name: this.args.channel.title,
      });
    }

    const channelName = `#${this.args.channel.title}`;

    if (this.args.channel.isFollowing) {
      return i18n("chat.channel.empty_state.joined_title", { channelName });
    }

    return i18n("chat.channel.empty_state.title", { channelName });
  }

  get tip() {
    if (this.isOneToOneDirectMessage) {
      return null;
    }

    if (this.args.channel.isFollowing) {
      return i18n("chat.channel.empty_state.joined_tip");
    }

    return i18n("chat.channel.empty_state.guest_tip");
  }

  @action
  loadMemberships() {
    if (!this.currentUser || !this.memberCount) {
      return;
    }

    this.memberships.load({ limit: MAX_AVATARS }).catch(() => {});
  }

  <template>
    {{#if this.currentUser}}
      <DEmptyState
        @body={{@channel.description}}
        @identifier="chat-channel"
        @svgContent={{this.channelIcon}}
        @title={{this.title}}
      >
        <:tip>
          {{#if this.tip}}
            <p class="empty-state__tip-text">{{this.tip}}</p>
          {{/if}}

          {{#unless this.isOneToOneDirectMessage}}
            <div
              class={{dConcatClass
                "empty-state__members-facepile"
                (if this.otherMemberships.length "--with-avatars")
              }}
              {{didInsert this.loadMemberships}}
            >
              {{#if this.otherMemberships.length}}
                <div class="empty-state__members-avatars">
                  {{#each this.otherMemberships as |membership|}}
                    {{dAvatar membership.user imageSize="small"}}
                  {{/each}}
                </div>
              {{/if}}
              <span class="empty-state__members-count">
                {{#if this.memberCount}}
                  {{trustHTML
                    (i18n
                      "chat.channel.empty_state.members_here"
                      count=this.memberCount
                    )
                  }}
                {{else}}
                  {{i18n "chat.channel.empty_state.no_other_members"}}
                {{/if}}
              </span>
            </div>
          {{/unless}}
        </:tip>
      </DEmptyState>
    {{/if}}
    {{#unless this.currentUser}}
      <DEmptyState
        @body={{@channel.description}}
        @identifier="chat-channel"
        @svgContent={{this.channelIcon}}
        @title={{this.title}}
      >
        <:tip>
          {{#if this.tip}}
            <p class="empty-state__tip-text">{{this.tip}}</p>
          {{/if}}
          <div class="empty-state__members-facepile">
            <span class="empty-state__members-count">
              {{#if this.memberCount}}
                {{trustHTML
                  (i18n
                    "chat.channel.empty_state.members_here"
                    count=this.memberCount
                  )
                }}
              {{else}}
                {{i18n "chat.channel.empty_state.no_other_members"}}
              {{/if}}
            </span>
          </div>
        </:tip>
      </DEmptyState>
    {{/unless}}
  </template>
}
